import { Formatters } from '../shared/lib/formatters';
import { LoadingSpinner } from '../shared/components';
import { supabase } from '../lib/supabase';

// Leads agent — MTR page → "Leads agent" tab (API: /api/leads/agent*).
//
// The agent is a small program on an always-on Mac: it keeps Zillow Rental
// Manager, Furnished Finder and Avail signed in inside its own Chrome, syncs
// their inboxes into the Lead inbox, and types the replies sent from there.
// This panel shows its health per platform, lets the office run a sync, and —
// the important part — opens a LIVE view of the agent's Chrome so a person can
// sign in / type a 2FA code / finish a "press & hold" check from anywhere.
//
// Lifecycle mirrors LeadsInbox: MtrPage keeps one instance and re-mounts it on
// every draw, so the unsaved settings form, notes and the live session live on
// the instance. The 15 s poll self-terminates when the root detaches.
//
// Live viewer notes: frames are pulled with fetch() (the /api Bearer header is
// added by main.ts — a plain <img src> would 401) → blob → object URL, always
// revoking the previous one. Input is batched and POSTed through one promise
// chain so events arrive in order (a quick down/up pair is a click; a long one
// is a press-and-hold).

const API = '/api/leads';
const POLL_MS = 15000;

type Tone = 'ok' | 'err' | 'info';
interface Note { text: string; tone: Tone; at?: number; }
interface ReqResult { ok: boolean; status: number; data: any; error: string; }
interface FocusMark { key: string; start: number | null; end: number | null; }

interface AgentInfo {
  online?: boolean; last_seen_at?: string | null; version?: string | null; host?: string | null; os?: string | null;
  platforms?: string[]; busy?: Record<string, string>; auto_login_ready?: Record<string, boolean>;
}
interface PlatformState {
  label?: string; short?: string; enabled?: boolean; state?: string; state_label?: string; message?: string;
  last_sync_at?: string | null; last_ok_at?: string | null; last_change_at?: string | null; inbox_url?: string;
  counts_last?: { threads_listed?: number; threads_fetched?: number; leads_new?: number; messages_new?: number } | null;
  has_screenshot?: boolean;
  live?: null | { on?: boolean; by?: string; since?: string; last_frame_at?: string | null };
}
interface Usage { calls?: number; input_tokens?: number; output_tokens?: number; cost_usd?: number; }
interface Config {
  enabled: boolean;
  interval_min: number | null; off_hours_interval_min: number | null;
  business_hours: { start: string; end: string };
  max_threads_per_sync: number | null;
  platforms: Record<string, { enabled: boolean; inbox_url: string; auto_login: boolean }>;
  sla_min: Record<string, number | null>;
  notify: { new_lead: boolean; breach: boolean; agent_problems: boolean };
  model: string;
  [k: string]: any;
}
interface AgentPayload {
  configured?: boolean; claude_configured?: boolean; can_edit?: boolean;
  agent?: AgentInfo | null;
  platforms?: Record<string, PlatformState>;
  config?: any;
  usage?: { today?: Usage; last_7d?: Usage; by_day?: Array<{ day: string; calls?: number; cost_usd?: number }> };
  runs?: Array<{ at?: string; platform?: string; kind?: string; status?: string; message?: string; duration_ms?: number | null; counts?: Record<string, any> | null }>;
  model?: string;
}
type LiveEvent =
  | { t: 'down' | 'up' | 'move'; x: number; y: number }
  | { t: 'wheel'; dy: number }
  | { t: 'text'; text: string }
  | { t: 'key'; key: string }
  | { t: 'nav'; url: string }
  | { t: 'back' } | { t: 'reload' };
interface LiveSession {
  pid: string; label: string;
  overlay: HTMLElement; screen: HTMLElement; img: HTMLImageElement; wait: HTMLElement;
  urlInput: HTMLInputElement; msg: HTMLElement; state: HTMLElement;
  seq: number; w: number; h: number; objUrl: string | null;
  closed: boolean; started: boolean; ended: boolean; frames: number;
  abort: AbortController | null;
  queue: LiveEvent[]; textBuf: string; textTimer: any; wheelDy: number; wheelTimer: any; sendTimer: any;
  chain: Promise<void>;
  down: boolean; lastMove: number; lastPt: { x: number; y: number } | null;
  detachedSince: number;
  takenOver: boolean;      // someone else took the session over — never post on:false for them
  notOwner: boolean;       // start refused with code 'in_use'
  token: string;           // cached access token for the pagehide keepalive beacon
  tokenAt: number;
  onHide: (() => void) | null;
}

const PLATFORMS = ['zillow', 'ff', 'avail'];
const PLAT_DEFAULTS: Record<string, { short: string; label: string }> = {
  zillow: { short: 'Zillow', label: 'Zillow Rental Manager' },
  ff: { short: 'FF', label: 'Furnished Finder' },
  avail: { short: 'Avail', label: 'Avail' },
};
const NEEDS_PERSON = ['login', 'two_factor', 'challenge'];
const SPECIAL_KEYS = new Set(['Enter', 'Tab', 'Escape', 'Backspace', 'Delete', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'PageUp', 'PageDown', 'Home', 'End']);
const BUSY_LABEL: Record<string, string> = { sync: 'syncing now…', send: 'sending a reply…', live: 'live session open', queued: 'sync queued' };
// PUT /agent/config accepts only these (server 400s anything else)
const MODELS = ['claude-opus-5', 'claude-opus-5-5', 'claude-sonnet-5', 'claude-opus-4-8', 'claude-fable-5', 'claude-fable-5-1'];
const DEFAULT_MODEL = 'claude-opus-5';
const COUNT_LABEL: Record<string, string> = { threads_listed: 'listed', threads_fetched: 'read', leads_new: 'new leads', messages_new: 'new msgs', replies_sent: 'sent' };

export class LeadsAgentPanel {
  private rootEl: HTMLElement | null = null;
  private timer: any = null;
  private ticking = false;
  private data: AgentPayload | null = null;
  private loadError = '';
  private seq = 0;

  // settings form — the edited copy survives re-mounts until saved/discarded
  private form: Config | null = null;
  private dirty = false;
  private saving = false;
  private saveNote: Note | null = null;
  private formBase: string | null = null;   // config.updated_at the form was loaded from → if_updated_at
  private staleSave = false;                // last save hit 409 {code:'stale'}

  private syncBusy = new Set<string>();
  private notes: Record<string, Note> = {};
  private setupOpen = false;
  private live: LiveSession | null = null;
  private shot: { overlay: HTMLElement; url: string | null; onKey: (e: KeyboardEvent) => void } | null = null;

  constructor(_host?: HTMLElement) { /* state-only; mount() renders */ }

  // ── lifecycle ─────────────────────────────────────────────────────────────
  async mount(host: HTMLElement): Promise<void> {
    this.stopPolling();
    host.innerHTML = '<div class="la"></div>';
    const root = host.querySelector<HTMLElement>('.la');
    if (!root) return;
    this.rootEl = root;
    this.bindRoot(root);
    this.draw();
    this.startPolling();
    await this.load();
  }

  // Veto App's 15-min refresh while settings are unsaved or a viewer is open.
  suspendAutoRefresh(): boolean { return this.dirty || this.saving || !!this.live || !!this.shot; }

  private startPolling(): void {
    this.stopPolling();
    this.timer = setInterval(() => { void this.tick(); }, POLL_MS);
  }
  private stopPolling(): void { if (this.timer) { clearInterval(this.timer); this.timer = null; } }
  private async tick(): Promise<void> {
    const root = this.rootEl;
    if (!root || !root.isConnected) { this.stopPolling(); this.closeShot(); return; }
    if (this.ticking || this.saving || document.hidden) return;
    this.ticking = true;
    try { await this.load(); } catch (e) { console.error('[leads-agent] poll failed', e); } finally { this.ticking = false; }
  }

  // ── helpers ───────────────────────────────────────────────────────────────
  private esc(s: any): string { return Formatters.escapeHtml(s == null ? '' : String(s)); }
  private num(v: any): string { const x = Number(v); return v != null && Number.isFinite(x) ? Math.round(x).toLocaleString('en-US') : '0'; }
  private money(v: any): string {
    const x = Number(v);
    if (!Number.isFinite(x) || x <= 0) return '$0.00';
    if (x < 0.01) return '<$0.01';
    return `$${x.toFixed(2)}`;
  }
  private ts(s: any): number { const t = Date.parse(String(s || '')); return Number.isFinite(t) ? t : 0; }
  private ago(s: any): string {
    const t = this.ts(s); if (!t) return '';
    const m = (Date.now() - t) / 60000;
    if (m < 1) return 'just now';
    if (m < 60) return `${Math.floor(m)}m ago`;
    if (m < 1440) return `${Math.floor(m / 60)}h ago`;
    if (m < 10080) return `${Math.floor(m / 1440)}d ago`;
    return `on ${new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
  }
  private full(s: any): string { const t = this.ts(s); return t ? new Date(t).toLocaleString('en-US') : ''; }
  private fmtDay(day: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(day || ''));
    if (!m) return String(day || '');
    return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }
  private sleep(ms: number): Promise<void> { return new Promise(r => setTimeout(r, ms)); }
  private pids(): string[] {
    const out: string[] = [...PLATFORMS];
    const more = [...Object.keys(this.data?.platforms || {}), ...Object.keys((this.data?.config && this.data.config.platforms) || {})];
    for (const k of more) if (/^[a-z0-9_]+$/i.test(k) && !out.includes(k)) out.push(k);
    return out;
  }
  private platLabel(pid: string): string {
    return String(this.data?.platforms?.[pid]?.label || PLAT_DEFAULTS[pid]?.label || pid);
  }
  private platShort(pid: string): string {
    return String(this.data?.platforms?.[pid]?.short || PLAT_DEFAULTS[pid]?.short || pid || '—');
  }
  private myEmail(): string {
    try { const p = JSON.parse(sessionStorage.getItem('userProfile') || '{}'); return String(p.email || '').trim().toLowerCase(); } catch { return ''; }
  }
  private loadForm(cfg: any): void {
    this.form = this.normConfig(cfg);
    this.formBase = cfg && cfg.updated_at != null ? String(cfg.updated_at) : null;
  }
  private freshNote(key: string): Note | null {
    const n = this.notes[key];
    if (!n) return null;
    const ttl = n.tone === 'err' ? 5 * 60000 : 90000;
    if (n.at && Date.now() - n.at > ttl) { delete this.notes[key]; return null; }
    return n;
  }

  private async req(method: string, url: string, body?: any): Promise<ReqResult> {
    try {
      const init: RequestInit = { method };
      if (body !== undefined) { init.headers = { 'Content-Type': 'application/json' }; init.body = JSON.stringify(body); }
      const r = await fetch(url, init);
      const data = await r.json().catch(() => null);
      if (!r.ok) {
        const msg = (data && typeof data.error === 'string' && data.error) ? data.error
          : r.status === 404 ? 'Not found (HTTP 404) — the leads agent API may not be deployed on this server yet.'
          : r.status === 403 ? 'Not allowed (HTTP 403) — your role can\'t do that here.'
          : `Request failed (HTTP ${r.status}).`;
        return { ok: false, status: r.status, data, error: msg };
      }
      return { ok: true, status: r.status, data, error: '' };
    } catch {
      return { ok: false, status: 0, data: null, error: 'Network error — check your connection and try again.' };
    }
  }

  private normConfig(raw: any): Config {
    let c: any = {};
    try { c = raw && typeof raw === 'object' ? JSON.parse(JSON.stringify(raw)) : {}; } catch { c = {}; }
    c.enabled = !!c.enabled;
    c.business_hours = { start: '', end: '', ...(c.business_hours && typeof c.business_hours === 'object' ? c.business_hours : {}) };
    c.platforms = c.platforms && typeof c.platforms === 'object' ? c.platforms : {};
    c.sla_min = c.sla_min && typeof c.sla_min === 'object' ? c.sla_min : {};
    c.notify = { new_lead: false, breach: false, agent_problems: false, ...(c.notify && typeof c.notify === 'object' ? c.notify : {}) };
    for (const pid of this.pids()) {
      c.platforms[pid] = { enabled: false, inbox_url: '', auto_login: false, ...(c.platforms[pid] || {}) };
      if (c.sla_min[pid] === undefined) c.sla_min[pid] = null;
    }
    if (!MODELS.includes(String(c.model || ''))) c.model = DEFAULT_MODEL;
    for (const k of ['interval_min', 'off_hours_interval_min', 'max_threads_per_sync']) if (c[k] === undefined) c[k] = null;
    return c as Config;
  }
  private setPath(obj: any, path: string, v: any): void {
    const parts = path.split('.');
    let o = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      if (!o[parts[i]] || typeof o[parts[i]] !== 'object') o[parts[i]] = {};
      o = o[parts[i]];
    }
    o[parts[parts.length - 1]] = v;
  }

  // ── data ──────────────────────────────────────────────────────────────────
  private async load(): Promise<void> {
    const seq = ++this.seq;
    const r = await this.req('GET', `${API}/agent`);
    if (seq !== this.seq) return;
    if (!r.ok || !r.data || typeof r.data !== 'object') {
      this.loadError = r.ok ? 'The server sent an unexpected answer.' : r.error;
      if (!this.data) this.draw(); else this.patch('head', this.headHtml());
      return;
    }
    const prev = this.data;
    this.data = r.data as AgentPayload;
    this.loadError = '';
    if (!this.dirty) this.loadForm(this.data.config);
    const root = this.rootEl;
    const structural = !prev || !root || !root.querySelector('[data-la-region="plats"]')
      || prev.configured !== this.data.configured || prev.claude_configured !== this.data.claude_configured || prev.can_edit !== this.data.can_edit;
    if (structural) { this.draw(); return; }
    this.patch('head', this.headHtml());
    this.patchPlats();
    this.patch('usage', this.usageHtml());
    this.patch('runs', this.runsHtml());
    if (!this.dirty && !this.settingsFocused()) this.patch('settings', this.settingsHtml());
  }

  // ── rendering ─────────────────────────────────────────────────────────────
  private draw(): void {
    const root = this.rootEl;
    if (!root || !root.isConnected) return;
    const focus = this.captureFocus(root);
    if (!this.data) {
      root.innerHTML = this.css() + (this.loadError
        ? `<div class="la-card la-empty"><div class="la-empty-ic">⚠️</div><h3>Couldn't load the Leads agent status</h3><p>${this.esc(this.loadError)}</p><button type="button" class="la-btn" data-la-act="refresh">Retry</button></div>`
        : `<div style="padding:48px; text-align:center;">${LoadingSpinner.render()}</div>`);
      return;
    }
    root.innerHTML = `${this.css()}
      <div data-la-region="head">${this.headHtml()}</div>
      ${this.data.claude_configured === false ? `<div class="la-callout warn">⚠️ <span><b>ANTHROPIC_API_KEY is missing on the server.</b> AI reply drafts in the Lead inbox (and anything else here that uses Claude) stay off until it's added in Railway → Variables.</span></div>` : ''}
      <div data-la-region="setup">${this.setupHtml()}</div>
      <div class="la-plats" data-la-region="plats">${this.platsHtml()}</div>
      <div class="la-cols">
        <div class="la-card" data-la-region="settings">${this.settingsHtml()}</div>
        <div class="la-card" data-la-region="usage">${this.usageHtml()}</div>
      </div>
      <div class="la-card" data-la-region="runs">${this.runsHtml()}</div>`;
    this.restoreFocus(root, focus);
  }

  private patch(region: string, html: string): HTMLElement | null {
    const root = this.rootEl;
    if (!root || !root.isConnected) return null;
    const el = root.querySelector<HTMLElement>(`[data-la-region="${region}"]`);
    if (!el) return null;
    const f = this.captureFocus(el);
    el.innerHTML = html;
    this.restoreFocus(el, f);
    return el;
  }
  private patchPlats(): void { this.patch('plats', this.platsHtml()); }
  private captureFocus(scope: HTMLElement): FocusMark | null {
    const ae = document.activeElement as HTMLInputElement | null;
    if (!ae || !scope.contains(ae) || !ae.dataset || !ae.dataset.laKey) return null;
    let start: number | null = null, end: number | null = null;
    try { start = ae.selectionStart ?? null; end = ae.selectionEnd ?? null; } catch { /* number/time/checkbox */ }
    return { key: ae.dataset.laKey, start, end };
  }
  private restoreFocus(scope: HTMLElement, f: FocusMark | null): void {
    if (!f) return;
    const el = Array.from(scope.querySelectorAll<HTMLInputElement>('[data-la-key]')).find(x => x.dataset.laKey === f.key);
    if (!el) return;
    try { el.focus({ preventScroll: true }); } catch { el.focus(); }
    if (f.start != null) { try { el.setSelectionRange(f.start, f.end ?? f.start); } catch { /* not a text control */ } }
  }
  private settingsFocused(): boolean {
    const el = this.rootEl?.querySelector('[data-la-region="settings"]');
    const ae = document.activeElement;
    return !!(el && ae && el.contains(ae) && /^(INPUT|SELECT|TEXTAREA)$/.test(ae.tagName));
  }

  private headHtml(): string {
    const d = this.data;
    if (!d) return '';
    const a = d.agent || null;
    const online = !!(a && a.online);
    const meta = a
      ? [a.last_seen_at ? `last seen ${this.ago(a.last_seen_at)}` : '', a.version ? `v${a.version}` : '', a.host || '', a.os || ''].filter(Boolean).join(' · ')
      : (d.configured ? "The agent hasn't connected yet — finish step 2 of the setup below." : 'Not set up yet — follow the setup steps below.');
    const paused = !!(d.config && d.config.enabled === false);
    return `
      <div class="la-card la-head">
        <span class="la-dot${online ? ' on' : ''}" aria-hidden="true"></span>
        <div class="la-head-t">
          <div class="la-head-title">Leads agent ${online ? 'online' : 'offline'}${paused ? ' <span class="la-chip grey">paused — master switch is off</span>' : ''}</div>
          <div class="la-meta" title="${this.esc(a && a.last_seen_at ? this.full(a.last_seen_at) : '')}">${this.esc(meta)}</div>
        </div>
        <div class="la-head-side">
          ${this.loadError ? `<span class="la-note err" title="${this.esc(this.loadError)}">⚠ couldn't refresh</span>` : ''}
          <button type="button" class="la-btn sm" data-la-act="refresh">↻ Refresh</button>
        </div>
        <p class="la-intro">A small program on an always-on Mac keeps Zillow Rental Manager, Furnished Finder and Avail signed in inside its own Chrome, brings new inquiries into the Lead inbox, and types the replies your team sends from there. When a platform wants a password, a code or a “press &amp; hold” check, use <b>Open live browser</b> below to do it from here.</p>
      </div>`;
  }

  private setupSteps(): string {
    const envFile = ['DASH_URL=https://westcoasthomestaysoperations.com', 'LEADS_AGENT_TOKEN=&lt;the same token&gt;'].join('\n');
    return `
      <ol class="la-steps">
        <li>
          <div class="la-step-t">Create a long random token and add it to Railway</div>
          <div class="la-meta">On any Mac, open Terminal and run:</div>
          <pre class="la-code"><code>openssl rand -hex 32 | pbcopy</code></pre>
          <div class="la-meta">That copies it without showing it. In Railway → this service → Variables, add it as <code>LEADS_AGENT_TOKEN</code>. Never paste the token into chat, email, Slack or a screenshot.</div>
        </li>
        <li>
          <div class="la-step-t">Install the agent on the always-on Mac</div>
          <div class="la-meta">A Mac mini is recommended — it must stay awake and stay signed in to macOS. Install Google Chrome and Node 22 or newer, get the repo onto it, then copy the agent into your <b>home folder</b>. macOS blocks background programs from reading Downloads, Desktop and Documents, so the agent can't run from there. Run this from the repo folder:</div>
          <pre class="la-code"><code>rsync -a agents/leads-agent/ ~/surge-leads-agent/ &amp;&amp; cd ~/surge-leads-agent &amp;&amp; npm install</code></pre>
          <div class="la-meta">Create the file <code>~/surge-leads-agent/.env</code> (<code>cp .env.example .env &amp;&amp; open -e .env</code>) containing:</div>
          <pre class="la-code"><code>${envFile}</code></pre>
          <div class="la-meta">Then, still in <code>~/surge-leads-agent</code>, run the installer — it starts the agent now and again at every login:</div>
          <pre class="la-code"><code>./deploy/install-macos.sh</code></pre>
        </li>
        <li>
          <div class="la-step-t">Sign in to each platform once</div>
          <div class="la-meta">Come back to this tab. Once the agent shows <b>online</b>, press <b>Open live browser</b> on each platform card and sign in to Zillow Rental Manager, Furnished Finder and Avail. The agent keeps those sessions from then on.</div>
        </li>
      </ol>`;
  }

  private setupHtml(): string {
    const d = this.data;
    if (!d) return '';
    if (!d.configured) {
      return `<div class="la-card"><div class="la-card-h">Set up the Leads agent <span class="la-chip warn">not configured</span><span class="la-meta">LEADS_AGENT_TOKEN isn't set on the server yet</span></div><div class="la-card-b">${this.setupSteps()}</div></div>`;
    }
    return `<details class="la-card la-setup-d" data-la-setup ${this.setupOpen ? 'open' : ''}><summary class="la-card-h">Setup / reinstall <span class="la-meta">how the agent Mac is set up</span></summary><div class="la-card-b">${this.setupSteps()}</div></details>`;
  }

  private stateChip(p: PlatformState, online: boolean): string {
    const st = String(p.state || '');
    let tone = 'grey', txt = '';
    if (st === 'disabled' || p.enabled === false) txt = 'Disabled';
    else if (st === 'offline' || !online) txt = 'Agent offline';
    else if (st === 'ok') { tone = 'ok'; txt = 'Connected'; }
    else if (NEEDS_PERSON.includes(st)) { tone = 'warn'; txt = p.state_label || (st === 'two_factor' ? 'Needs a sign-in code' : st === 'challenge' ? 'Needs a human check' : 'Needs sign-in'); }
    else if (st === 'error') { tone = 'bad'; txt = p.state_label || 'Error'; }
    else if (st === 'never_run') txt = 'Not run yet';
    else txt = p.state_label || st || 'Unknown';
    return `<span class="la-chip ${tone}">${this.esc(txt)}</span>`;
  }

  private countsTxt(c: Record<string, any> | null | undefined): string {
    if (!c || typeof c !== 'object') return '';
    return Object.keys(c)
      .filter(k => c[k] != null && (typeof c[k] === 'number' || typeof c[k] === 'string'))
      .map(k => `${c[k]} ${COUNT_LABEL[k] || k.replace(/_/g, ' ')}`)
      .join(' · ');
  }

  private platsHtml(): string {
    const d = this.data;
    if (!d) return '';
    return this.pids().filter(pid => PLATFORMS.includes(pid) || d.platforms?.[pid]).map(pid => this.platCard(pid)).join('');
  }

  private platCard(pid: string): string {
    const d = this.data!;
    const p: PlatformState = d.platforms?.[pid] || {};
    const online = !!(d.agent && d.agent.online);
    const busy = d.agent?.busy?.[pid] || '';
    const busyTxt = busy && busy !== 'idle' ? (BUSY_LABEL[busy] || busy) : '';
    const counts = this.countsTxt(p.counts_last as any);
    const note = this.freshNote(pid);
    const live = p.live && p.live.on ? p.live : null;
    const syncing = this.syncBusy.has(pid);
    const me = this.myEmail();
    const otherUser = !!(live && live.by && String(live.by).trim().toLowerCase() !== me && !(this.live && this.live.pid === pid));
    return `
      <div class="la-card la-plat">
        <div class="la-plat-top"><div class="la-plat-name">${this.esc(this.platLabel(pid))}</div>${this.stateChip(p, online)}</div>
        <div class="la-meta">${p.enabled ? 'Enabled' : 'Turned off in settings'}${busyTxt ? ` · <b class="la-busy">${this.esc(busyTxt)}</b>` : ''}</div>
        ${p.message ? `<div class="la-plat-msg">${this.esc(p.message)}</div>` : ''}
        <dl class="la-kv">
          <dt>Last sync</dt><dd title="${this.esc(this.full(p.last_sync_at))}">${p.last_sync_at ? this.esc(this.ago(p.last_sync_at)) : '<span class="la-faint">never</span>'}</dd>
          <dt>Last OK</dt><dd title="${this.esc(this.full(p.last_ok_at))}">${p.last_ok_at ? this.esc(this.ago(p.last_ok_at)) : '<span class="la-faint">—</span>'}</dd>
          <dt>Last run</dt><dd>${counts ? this.esc(counts) : '<span class="la-faint">—</span>'}</dd>
        </dl>
        ${live ? `<div class="la-plat-live">● Live session open${live.by ? ` — ${this.esc(live.by)}` : ''}${live.since ? ` · since ${this.esc(this.ago(live.since))}` : ''}</div>` : ''}
        <div class="la-plat-actions">
          <button type="button" class="la-btn sm" data-la-act="sync" data-la-pid="${this.esc(pid)}" ${syncing ? 'disabled' : ''}>${syncing ? 'Requesting…' : '↻ Sync now'}</button>
          ${otherUser
            ? `<button type="button" class="la-btn sm" disabled title="${this.esc(`${live!.by} has the live browser open${live!.since ? ` since ${this.ago(live!.since)}` : ''}`)}">🖥 In use by ${this.esc(live!.by)}</button><button type="button" class="la-btn sm la-takeover" data-la-act="takeover" data-la-pid="${this.esc(pid)}" ${this.live ? 'disabled' : ''}>Take over</button>`
            : `<button type="button" class="la-btn sm" data-la-act="live" data-la-pid="${this.esc(pid)}" ${this.live ? 'disabled' : ''} title="See and control the agent's Chrome for ${this.esc(this.platLabel(pid))}">🖥 Open live browser</button>`}
          ${p.has_screenshot ? `<button type="button" class="la-btn sm" data-la-act="shot" data-la-pid="${this.esc(pid)}">Last problem screenshot</button>` : ''}
        </div>
        ${note ? `<div class="la-note ${note.tone}" role="status">${this.esc(note.text)}</div>` : ''}
      </div>`;
  }

  private settingsHtml(): string {
    const d = this.data;
    const f = this.form;
    if (!d || !f) return `<div class="la-card-h">Settings</div><div class="la-card-b la-meta">Settings aren't available.</div>`;
    const can = !!d.can_edit;
    const dis = can ? '' : 'disabled';
    const key = (path: string) => `data-la-cfg="${this.esc(path)}" data-la-key="cfg-${this.esc(path)}"`;
    const num = (path: string, val: any, attrs = '') => `<input class="la-in" type="number" min="1" step="1" inputmode="numeric" ${key(path)} data-la-type="num" value="${val == null ? '' : this.esc(val)}" ${dis} ${attrs}>`;
    const chk = (path: string, val: any, attrs = '') => `<input type="checkbox" ${key(path)} data-la-type="bool" ${val ? 'checked' : ''} ${dis} ${attrs}>`;
    const txt = (path: string, val: any, attrs = '', cls = 'la-in') => `<input class="${cls}" type="text" ${key(path)} data-la-type="text" value="${this.esc(val || '')}" ${dis} ${attrs}>`;
    const time = (path: string, val: any) => `<input class="la-in" type="time" ${key(path)} data-la-type="text" value="${this.esc(val || '')}" ${dis}>`;
    const ready = d.agent?.auto_login_ready || {};
    const rows = this.pids().map(pid => {
      const pc = f.platforms[pid] || { enabled: false, inbox_url: '', auto_login: false };
      const label = this.platLabel(pid);
      const r = ready[pid];
      return `
        <tr>
          <td class="la-ptbl-name">${this.esc(label)}</td>
          <td>${chk(`platforms.${pid}.enabled`, pc.enabled, `aria-label="${this.esc(label)} enabled"`)}</td>
          <td>${txt(`platforms.${pid}.inbox_url`, pc.inbox_url, `placeholder="https://…" spellcheck="false" aria-label="${this.esc(label)} inbox URL"`, 'la-in la-url')}</td>
          <td>${num(`sla_min.${pid}`, f.sla_min[pid], `aria-label="${this.esc(label)} response target in minutes" style="width:84px;"`)}</td>
          <td><label class="la-inline">${chk(`platforms.${pid}.auto_login`, pc.auto_login, `title="Only works if this platform's email and password are set on the agent computer (never in the dashboard). Off by default."`)}<span class="la-meta">${r === true ? 'credentials on agent ✓' : r === false ? 'no credentials on agent' : ''}</span></label></td>
        </tr>`;
    }).join('');
    return `
      <div class="la-card-h">Settings ${can ? '' : '<span class="la-meta">view only — admins, directors and managers can change these</span>'}</div>
      <div class="la-card-b la-form">
        <label class="la-switch">${chk('enabled', f.enabled)}<span><b>Agent enabled</b><span class="la-meta">Master switch — when off, nothing syncs and no replies are sent on any platform.</span></span></label>
        <div class="la-sub-h">Schedule</div>
        <div class="la-fgrid">
          <label>Sync every (min) — business hours${num('interval_min', f.interval_min)}</label>
          <label>Sync every (min) — off hours${num('off_hours_interval_min', f.off_hours_interval_min)}</label>
          <label>Max threads per sync${num('max_threads_per_sync', f.max_threads_per_sync)}</label>
          <label>Business hours start (Pacific)${time('business_hours.start', f.business_hours.start)}</label>
          <label>Business hours end (Pacific)${time('business_hours.end', f.business_hours.end)}</label>
          <label>AI model<select class="la-in" ${key('model')} data-la-type="text" ${dis}>${MODELS.map(m => `<option value="${this.esc(m)}" ${m === f.model ? 'selected' : ''}>${this.esc(m)}${m === DEFAULT_MODEL ? ' (default)' : ''}</option>`).join('')}</select>${d.config && d.config.model && !MODELS.includes(String(d.config.model)) ? `<span class="la-meta" style="font-weight:400;">Saved model “${this.esc(d.config.model)}” isn't offered any more — saving switches to ${DEFAULT_MODEL}.</span>` : ''}</label>
        </div>
        <div class="la-sub-h">Platforms</div>
        <div class="la-tbl-wrap">
          <table class="la-tbl la-ptbl">
            <thead><tr><th>Platform</th><th>On</th><th>Inbox URL</th><th>Response target (min)</th><th>Auto sign-in</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
        <div class="la-meta la-help">Auto sign-in: Only works if this platform's email and password are set on the agent computer (never in the dashboard). Off by default.</div>
        <div class="la-sub-h">Notifications</div>
        <div class="la-checks">
          <label class="la-inline">${chk('notify.new_lead', f.notify.new_lead)}<span>New lead</span></label>
          <label class="la-inline">${chk('notify.breach', f.notify.breach)}<span>Response-target breach</span></label>
          <label class="la-inline">${chk('notify.agent_problems', f.notify.agent_problems)}<span>Agent problems (sign-in needed, errors, offline)</span></label>
        </div>
      </div>
      ${can ? `<div data-la-region="savebar">${this.savebarHtml()}</div>` : ''}`;
  }

  private savebarHtml(): string {
    const n = this.saveNote;
    const txt = n ? `<span class="la-note ${n.tone}" role="status">${this.esc(n.text)}</span>`
      : this.dirty ? `<span class="la-note info">Unsaved changes</span>` : `<span class="la-note"></span>`;
    return `<div class="la-savebar">${txt}${this.staleSave ? `<button type="button" class="la-btn sm primary" data-la-act="reload-settings" title="Discard your edits and load the saved settings">Reload</button>` : ''}<button type="button" class="la-btn sm" data-la-act="discard" ${!this.dirty || this.saving ? 'disabled' : ''}>Discard</button><button type="button" class="la-btn sm${this.staleSave ? '' : ' primary'}" data-la-act="save" ${!this.dirty || this.saving || this.staleSave ? 'disabled' : ''}>${this.saving ? 'Saving…' : 'Save settings'}</button></div>`;
  }

  private usageHtml(): string {
    const d = this.data;
    if (!d) return '';
    const u = d.usage || {};
    const today = u.today || {}, week = u.last_7d || {};
    const days = (Array.isArray(u.by_day) ? u.by_day : []).filter(x => x && x.day).slice().sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0)).slice(-14);
    const useCost = days.some(x => Number(x.cost_usd) > 0);
    const val = (x: any) => { const v = Number(useCost ? x.cost_usd : x.calls); return Number.isFinite(v) && v > 0 ? v : 0; };
    const max = days.reduce((m, x) => Math.max(m, val(x)), 0);
    const bars = days.map((x, i) => {
      const h = max ? Math.max(4, Math.round(val(x) / max * 100)) : 4;
      const tip = `${this.fmtDay(x.day)} · ${this.num(x.calls)} calls · ${this.money(x.cost_usd)}`;
      return `<div class="la-bar-c" title="${this.esc(tip)}"><div class="la-bar${i === days.length - 1 ? ' last' : ''}${val(x) ? '' : ' zero'}" style="height:${h}%"></div></div>`;
    }).join('');
    const tokens = this.n(week.input_tokens) + this.n(week.output_tokens);
    return `
      <div class="la-card-h">AI usage <span class="la-meta">${this.esc(d.model || '')}</span></div>
      <div class="la-card-b">
        <div class="la-usage">
          <div><div class="la-u-l">Today</div><div class="la-u-v">${this.esc(this.money(today.cost_usd))}</div><div class="la-meta">${this.num(today.calls)} call${this.n(today.calls) === 1 ? '' : 's'}</div></div>
          <div><div class="la-u-l">Last 7 days</div><div class="la-u-v">${this.esc(this.money(week.cost_usd))}</div><div class="la-meta">${this.num(week.calls)} call${this.n(week.calls) === 1 ? '' : 's'}${tokens ? ` · ${this.num(tokens)} tokens` : ''}</div></div>
        </div>
        ${days.length ? `
          <div class="la-bars" role="img" aria-label="${useCost ? 'Daily AI cost' : 'Daily AI calls'}, last ${days.length} days">${bars}</div>
          <div class="la-bars-x"><span>${this.esc(this.fmtDay(days[0].day))}</span><span>${useCost ? 'daily cost' : 'daily calls'}</span><span>${this.esc(this.fmtDay(days[days.length - 1].day))}</span></div>`
          : `<div class="la-meta" style="margin-top:14px;">No AI calls in the last 14 days.</div>`}
        <div class="la-meta" style="margin-top:10px;">Estimated from token counts — Anthropic's invoice is the source of truth.</div>
      </div>`;
  }
  private n(v: any): number { const x = Number(v); return Number.isFinite(x) ? x : 0; }

  private runTone(s: string): string {
    const v = String(s || '').toLowerCase();
    if (['ok', 'success', 'done', 'sent', 'completed', 'complete'].includes(v)) return 'ok';
    if (['error', 'failed', 'fail', 'crash', 'crashed'].includes(v)) return 'bad';
    if (['login', 'two_factor', 'challenge', 'uncertain', 'blocked', 'partial', 'warn', 'warning', 'timeout', 'skipped'].includes(v)) return 'warn';
    return 'grey';
  }

  private runsHtml(): string {
    const runs = (this.data?.runs || []).filter(Boolean).slice(0, 50);
    const dur = (ms: any) => { const x = Number(ms); if (ms == null || !Number.isFinite(x)) return '—'; return x < 1000 ? `${Math.round(x)} ms` : x < 60000 ? `${(x / 1000).toFixed(1)} s` : `${Math.floor(x / 60000)}m ${Math.round((x % 60000) / 1000)}s`; };
    const rows = runs.map(r => `
      <tr>
        <td class="la-nowrap" title="${this.esc(this.full(r.at))}">${this.esc(this.ago(r.at) || '—')}</td>
        <td class="la-nowrap">${this.esc(r.platform ? this.platShort(r.platform) : '—')}</td>
        <td>${this.esc(r.kind || '—')}</td>
        <td><span class="la-chip ${this.runTone(String(r.status || ''))}">${this.esc(r.status || '—')}</span></td>
        <td class="la-run-msg" title="${this.esc(r.message || '')}">${this.esc(r.message || '')}</td>
        <td class="la-nowrap la-num">${this.esc(dur(r.duration_ms))}</td>
        <td class="la-meta">${this.esc(this.countsTxt(r.counts))}</td>
      </tr>`).join('');
    return `
      <div class="la-card-h">Recent runs <span class="la-meta">${runs.length ? `newest first · last ${runs.length}` : ''}</span></div>
      ${runs.length
        ? `<div class="la-tbl-wrap la-runs"><table class="la-tbl"><thead><tr><th>When</th><th>Platform</th><th>Kind</th><th>Status</th><th>Message</th><th>Duration</th><th>Counts</th></tr></thead><tbody>${rows}</tbody></table></div>`
        : `<div class="la-card-b la-meta">No runs yet — syncs, sends and live sessions show up here once the agent is running.</div>`}`;
  }

  // ── events ────────────────────────────────────────────────────────────────
  private bindRoot(root: HTMLElement): void {
    root.addEventListener('click', (e) => this.onClick(e));
    root.addEventListener('input', (e) => this.onCfg(e));
    root.addEventListener('change', (e) => this.onCfg(e));
    root.addEventListener('toggle', (e) => { const t = e.target as HTMLDetailsElement; if (t && t.dataset && 'laSetup' in t.dataset) this.setupOpen = t.open; }, true);
  }
  private run(p: Promise<unknown>): void { p.catch(err => console.error('[leads-agent]', err)); }

  private onClick(e: Event): void {
    const el = (e.target as HTMLElement | null)?.closest<HTMLElement>('[data-la-act]');
    if (!el || !this.rootEl || !this.rootEl.contains(el) || (el as HTMLButtonElement).disabled) return;
    const pid = el.dataset.laPid || '';
    switch (el.dataset.laAct) {
      case 'refresh': this.loadError = ''; if (!this.data) this.draw(); this.run(this.load()); break;
      case 'sync': if (pid) this.run(this.sync(pid)); break;
      case 'live': if (pid) this.run(this.openLive(pid, false)); break;
      case 'takeover': {
        const by = this.data?.platforms?.[pid]?.live?.by || 'Someone else';
        if (pid && confirm(`${by} is using the live browser for ${this.platLabel(pid)}. Take over? Their view stops.`)) this.run(this.openLive(pid, true));
        break;
      }
      case 'reload-settings': this.run(this.reloadSettings()); break;
      case 'shot': if (pid) this.run(this.showShot(pid)); break;
      case 'save': this.run(this.save()); break;
      case 'discard':
        this.dirty = false;
        this.saveNote = null;
        this.staleSave = false;
        this.loadForm(this.data?.config);
        this.patch('settings', this.settingsHtml());
        break;
    }
  }

  private onCfg(e: Event): void {
    const t = e.target as HTMLInputElement;
    if (!t || !t.dataset || !t.dataset.laCfg || !this.form || !this.data?.can_edit) return;
    const type = t.dataset.laType;
    let v: any;
    if (type === 'bool') v = !!t.checked;
    else if (type === 'num') v = t.value === '' ? null : Number(t.value);
    else v = t.value;
    this.setPath(this.form, t.dataset.laCfg, v);
    const wasDirty = this.dirty;
    this.dirty = true;
    if (this.saveNote || !wasDirty) { this.saveNote = null; this.patch('savebar', this.savebarHtml()); }
  }

  // ── actions ───────────────────────────────────────────────────────────────
  private validate(c: Config): string {
    const pos = (v: any) => v != null && v !== '' && Number.isFinite(Number(v)) && Number(v) > 0;
    if (!pos(c.interval_min)) return 'Business-hours sync interval must be a positive number of minutes.';
    if (!pos(c.off_hours_interval_min)) return 'Off-hours sync interval must be a positive number of minutes.';
    if (!pos(c.max_threads_per_sync)) return 'Max threads per sync must be a positive number.';
    const hm = /^([01]\d|2[0-3]):[0-5]\d(:[0-5]\d)?$/;
    if (!hm.test(String(c.business_hours.start || '')) || !hm.test(String(c.business_hours.end || ''))) return 'Set both business-hours times (HH:MM, Pacific).';
    for (const pid of this.pids()) {
      const label = this.platLabel(pid);
      if (!pos(c.sla_min[pid])) return `${label}: the response target must be a positive number of minutes.`;
      const url = String(c.platforms[pid]?.inbox_url || '').trim();
      if (url && !/^https?:\/\/\S+$/i.test(url)) return `${label}: the inbox URL must start with https://`;
    }
    return '';
  }

  private async save(): Promise<void> {
    if (!this.form || this.saving || !this.data?.can_edit) return;
    const err = this.validate(this.form);
    if (err) { this.saveNote = { text: err, tone: 'err' }; this.patch('savebar', this.savebarHtml()); return; }
    const body: any = JSON.parse(JSON.stringify(this.form));
    for (const k of ['interval_min', 'off_hours_interval_min', 'max_threads_per_sync']) body[k] = Math.round(Number(body[k]));
    for (const pid of Object.keys(body.sla_min || {})) if (body.sla_min[pid] != null) body.sla_min[pid] = Math.round(Number(body.sla_min[pid]));
    for (const pid of Object.keys(body.platforms || {})) body.platforms[pid].inbox_url = String(body.platforms[pid].inbox_url || '').trim();
    body.model = MODELS.includes(String(body.model || '')) ? body.model : DEFAULT_MODEL;
    delete body.updated_at;
    delete body.if_updated_at;
    if (this.formBase) body.if_updated_at = this.formBase;   // optimistic concurrency: 409 {code:'stale'} if someone saved since
    this.saving = true;
    this.saveNote = null;
    this.patch('savebar', this.savebarHtml());
    let r: ReqResult;
    try { r = await this.req('PUT', `${API}/agent/config`, body); } finally { this.saving = false; }
    if (!r.ok) {
      if (r.status === 409 && r.data && r.data.code === 'stale') {
        this.staleSave = true;
        this.saveNote = { text: 'Someone else changed the settings since you opened them — reload to see their changes.', tone: 'err' };
      } else this.saveNote = { text: `Not saved — ${r.error}`, tone: 'err' };
      this.patch('savebar', this.savebarHtml());
      return;
    }
    this.dirty = false;
    this.staleSave = false;
    const cfg = r.data && r.data.config ? r.data.config : body;
    if (this.data) this.data.config = cfg;
    this.loadForm(cfg);
    this.saveNote = { text: 'Saved ✓ — the agent picks this up on its next check-in.', tone: 'ok' };
    this.patch('settings', this.settingsHtml());
    this.patch('head', this.headHtml());
    void this.load();
  }

  // Stale save → drop local edits and load what's saved now.
  private async reloadSettings(): Promise<void> {
    this.dirty = false;
    this.staleSave = false;
    this.saveNote = null;
    await this.load();
    this.loadForm(this.data?.config);
    this.saveNote = { text: 'Reloaded the saved settings — make your changes again if they still apply.', tone: 'info' };
    this.patch('settings', this.settingsHtml());
  }

  private async sync(pid: string): Promise<void> {
    if (this.syncBusy.has(pid)) return;
    this.syncBusy.add(pid);
    delete this.notes[pid];
    this.patchPlats();
    const r = await this.req('POST', `${API}/agent/sync`, { platform: pid });
    this.syncBusy.delete(pid);
    this.notes[pid] = r.ok
      ? { text: 'Sync requested — results show here within a minute or two.', tone: 'ok', at: Date.now() }
      : { text: `Sync not requested — ${r.error}`, tone: 'err', at: Date.now() };
    this.patchPlats();
    if (r.ok) setTimeout(() => { if (this.rootEl && this.rootEl.isConnected) void this.load(); }, 5000);
  }

  // ── problem screenshot modal ──────────────────────────────────────────────
  private async showShot(pid: string): Promise<void> {
    this.closeShot();
    const label = this.platLabel(pid);
    const overlay = document.createElement('div');
    overlay.className = 'la-live-bg';
    overlay.innerHTML = `${this.liveCss()}
      <div class="la-live la-shot" role="dialog" aria-modal="true" aria-label="${this.esc(`Last problem screenshot — ${label}`)}">
        <div class="la-live-head"><div class="la-live-title">Last problem screenshot · ${this.esc(label)}</div><button type="button" class="la-lbtn" data-shot="close">Close</button></div>
        <div class="la-shot-body" data-shot="body"><div class="la-live-wait">Loading…</div></div>
      </div>`;
    document.body.appendChild(overlay);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); this.closeShot(); } };
    document.addEventListener('keydown', onKey, true);
    const S = { overlay, url: null as string | null, onKey };
    this.shot = S;
    overlay.addEventListener('click', (e) => {
      const t = e.target as HTMLElement;
      if (t === overlay || t.closest('[data-shot="close"]')) this.closeShot();
    });
    const body = overlay.querySelector<HTMLElement>('[data-shot="body"]')!;
    let r: Response | null = null;
    try { r = await fetch(`${API}/agent/shot/${encodeURIComponent(pid)}`, { cache: 'no-store' }); } catch { r = null; }
    if (this.shot !== S) return;
    if (!r || !r.ok) {
      const j = r ? await r.json().catch(() => null) : null;
      if (this.shot !== S) return;
      const msg = r && r.status === 404 ? 'No problem screenshot is saved for this platform.'
        : `Couldn't load the screenshot — ${(j && j.error) || (r ? `HTTP ${r.status}` : 'network error')}`;
      body.innerHTML = `<div class="la-live-wait">${this.esc(msg)}</div>`;
      return;
    }
    const blob = await r.blob().catch(() => null);
    if (this.shot !== S) return;
    if (!blob || !blob.size) { body.innerHTML = `<div class="la-live-wait">The screenshot was empty.</div>`; return; }
    S.url = URL.createObjectURL(blob);
    const img = document.createElement('img');
    img.className = 'la-shot-img';
    img.alt = `Last problem page on ${label}`;
    img.src = S.url;
    body.innerHTML = '';
    body.appendChild(img);
  }
  private closeShot(): void {
    const S = this.shot;
    if (!S) return;
    this.shot = null;
    document.removeEventListener('keydown', S.onKey, true);
    S.overlay.remove();
    if (S.url) URL.revokeObjectURL(S.url);
  }

  // ── live viewer ───────────────────────────────────────────────────────────
  private async openLive(pid: string, takeover: boolean): Promise<void> {
    if (this.live) return;
    const L = this.buildLive(pid, this.platLabel(pid));
    this.live = L;
    this.patchPlats();
    this.refreshToken(L);
    this.liveMsg(L, takeover ? 'Taking over the live session…' : 'Starting a live session on the agent…', 'info');
    const r = await this.req('POST', `${API}/agent/live`, takeover ? { platform: pid, on: true, takeover: true } : { platform: pid, on: true });
    if (L.closed) return;
    if (!r.ok) {
      const inUse = r.status === 409 && !!r.data && r.data.code === 'in_use';
      if (inUse) L.notOwner = true;
      this.liveState(L, inUse ? 'in use' : 'not connected');
      this.liveMsg(L, inUse ? `${r.error} Close this and use “Take over” on the card if you need it.` : r.status === 409 ? r.error : `Couldn't start the live session — ${r.error}`, 'err');
      L.wait.textContent = inUse
        ? `${(r.data && r.data.by) || 'Someone else'} is using the live browser right now.`
        : r.status === 409 ? 'The agent has to be running on the agent Mac before you can sign in from here.' : 'No live session.';
      if (inUse) void this.load();   // the card flips to "In use by …"
      return;
    }
    L.started = true;
    // best-effort: tell the server we left if the tab closes / navigates away
    L.onHide = () => this.liveBeacon(L);
    window.addEventListener('pagehide', L.onHide);
    this.liveState(L, 'connecting…');
    this.liveMsg(L, "Click inside the page, then type — your keys go to the agent's Chrome. Use ⌨ Type text… to paste a code.", 'info');
    void this.frameLoop(L);
    try { L.screen.focus({ preventScroll: true }); } catch { /* ignore */ }
  }

  private buildLive(pid: string, label: string): LiveSession {
    const overlay = document.createElement('div');
    overlay.className = 'la-live-bg';
    overlay.innerHTML = `${this.liveCss()}
      <div class="la-live" role="dialog" aria-modal="true" aria-label="${this.esc(`Live browser — ${label}`)}">
        <div class="la-live-head">
          <div class="la-live-title">🖥 Live browser · ${this.esc(label)}<span class="la-live-state" data-live="state">starting…</span></div>
          <button type="button" class="la-lbtn" data-live="close">Close</button>
        </div>
        <div class="la-live-bar">
          <button type="button" class="la-lbtn" data-live="back" title="Back">◀ Back</button>
          <button type="button" class="la-lbtn" data-live="reload" title="Reload the page">↻ Reload</button>
          <input class="la-live-url" data-live="url" spellcheck="false" autocomplete="off" placeholder="Page address" aria-label="Page address">
          <button type="button" class="la-lbtn" data-live="go">Go</button>
          <button type="button" class="la-lbtn" data-live="type" title="Type or paste text into the field that has focus on the page">⌨ Type text…</button>
        </div>
        <div class="la-live-type" data-live="typerow" hidden>
          <input type="text" class="la-live-url" data-live="typeinput" autocomplete="off" spellcheck="false" placeholder="Text to type into the field that has focus on the page (e.g. a sign-in code)">
          <button type="button" class="la-lbtn primary" data-live="typesend">Type it</button>
          <button type="button" class="la-lbtn" data-live="typecancel">Cancel</button>
        </div>
        <div class="la-live-hint">You are controlling the agent's Chrome. Sign in, finish any code or 'press &amp; hold' check, then press Done.</div>
        <div class="la-live-screen" data-live="screen" tabindex="0" aria-label="Remote page — click to interact; while focused, keys go to the remote page">
          <img data-live="img" alt="Live view of the agent's browser" draggable="false">
          <div class="la-live-wait" data-live="wait">Waiting for the first frame…</div>
        </div>
        <div class="la-live-foot">
          <span class="la-live-msg" data-live="msg" role="status"></span>
          <button type="button" class="la-lbtn primary" data-live="done">Done — I'm signed in</button>
          <button type="button" class="la-lbtn" data-live="close">Close</button>
        </div>
      </div>`;
    document.body.appendChild(overlay);
    const q = <T extends HTMLElement>(k: string) => overlay.querySelector<T>(`[data-live="${k}"]`)!;
    const L: LiveSession = {
      pid, label, overlay,
      screen: q<HTMLElement>('screen'), img: q<HTMLImageElement>('img'), wait: q<HTMLElement>('wait'),
      urlInput: q<HTMLInputElement>('url'), msg: q<HTMLElement>('msg'), state: q<HTMLElement>('state'),
      seq: 0, w: 0, h: 0, objUrl: null,
      closed: false, started: false, ended: false, frames: 0,
      abort: null,
      queue: [], textBuf: '', textTimer: null, wheelDy: 0, wheelTimer: null, sendTimer: null,
      chain: Promise.resolve(),
      down: false, lastMove: 0, lastPt: null,
      detachedSince: 0,
      takenOver: false, notOwner: false, token: '', tokenAt: 0, onHide: null,
    };

    // buttons
    overlay.addEventListener('click', (e) => {
      const b = (e.target as HTMLElement).closest<HTMLElement>('button[data-live]');
      if (!b) return;
      switch (b.dataset.live) {
        case 'close': void this.closeLive(L, 'close'); break;
        case 'done': void this.closeLive(L, 'done'); break;
        case 'back': this.liveEnqueue(L, { t: 'back' }); this.focusScreen(L); break;
        case 'reload': this.liveEnqueue(L, { t: 'reload' }); this.focusScreen(L); break;
        case 'go': this.liveGo(L); break;
        case 'type': {
          // Inline box (no prompt(): it is blocked in some browsers and
          // would show a code in a native dialog).
          const row = overlay.querySelector<HTMLElement>('[data-live="typerow"]');
          const inp = overlay.querySelector<HTMLInputElement>('[data-live="typeinput"]');
          if (row && inp) { row.hidden = false; inp.value = ''; inp.focus(); }
          break;
        }
        case 'typesend': {
          const row = overlay.querySelector<HTMLElement>('[data-live="typerow"]');
          const inp = overlay.querySelector<HTMLInputElement>('[data-live="typeinput"]');
          const text = inp ? inp.value : '';
          if (text) this.liveEnqueue(L, { t: 'text', text });
          if (inp) inp.value = '';
          if (row) row.hidden = true;
          this.focusScreen(L);
          break;
        }
        case 'typecancel': {
          const row = overlay.querySelector<HTMLElement>('[data-live="typerow"]');
          const inp = overlay.querySelector<HTMLInputElement>('[data-live="typeinput"]');
          if (inp) inp.value = '';
          if (row) row.hidden = true;
          this.focusScreen(L);
          break;
        }
      }
    });
    // keep app-level shortcuts out of the modal's own inputs
    const typeInput = overlay.querySelector<HTMLInputElement>('[data-live="typeinput"]');
    typeInput?.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') { e.preventDefault(); overlay.querySelector<HTMLElement>('[data-live="typesend"]')?.click(); }
      else if (e.key === 'Escape') { e.preventDefault(); overlay.querySelector<HTMLElement>('[data-live="typecancel"]')?.click(); }
    });
    L.urlInput.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') { e.preventDefault(); this.liveGo(L); }
      else if (e.key === 'Escape') { e.preventDefault(); L.urlInput.blur(); }
    });

    // pointer → frame pixels (x = offsetX * frameW / displayed width)
    const img = L.img;
    img.addEventListener('pointerdown', (e) => {
      if (e.button !== 0 || !L.started || L.closed) return;
      e.preventDefault();
      this.focusScreen(L);
      try { img.setPointerCapture(e.pointerId); } catch { /* ignore */ }
      const p = this.framePoint(L, e.clientX, e.clientY);
      if (!p) return;
      L.down = true; L.lastPt = p;
      this.liveEnqueue(L, { t: 'down', x: p.x, y: p.y });
    });
    img.addEventListener('pointermove', (e) => {
      if (!L.down) return;
      const now = Date.now();
      if (now - L.lastMove < 60) return;   // drags (sliders) only — no hover spam
      L.lastMove = now;
      const p = this.framePoint(L, e.clientX, e.clientY);
      if (!p) return;
      L.lastPt = p;
      this.liveEnqueue(L, { t: 'move', x: p.x, y: p.y });
    });
    const up = (e: PointerEvent) => {
      if (!L.down) return;
      L.down = false;
      const p = this.framePoint(L, e.clientX, e.clientY) || L.lastPt;
      if (p) this.liveEnqueue(L, { t: 'up', x: p.x, y: p.y });
    };
    img.addEventListener('pointerup', up);
    img.addEventListener('pointercancel', up);
    img.addEventListener('lostpointercapture', (e) => up(e as PointerEvent));
    img.addEventListener('wheel', (e) => {
      e.preventDefault();
      if (!L.started || L.closed) return;
      const mult = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? (L.h || 800) : 1;
      this.liveWheel(L, e.deltaY * mult);
    }, { passive: false });
    img.addEventListener('dragstart', (e) => e.preventDefault());
    img.addEventListener('contextmenu', (e) => e.preventDefault());

    // keyboard: only while the screen has focus. preventDefault + stopPropagation
    // so the app's global Cmd+K / Escape handlers never fire — Escape belongs
    // to the remote page (the modal closes only via its buttons).
    L.screen.addEventListener('keydown', (e) => {
      if (L.closed || e.isComposing) return;
      const k = e.key;
      if (e.metaKey || e.ctrlKey) {
        if (k === 'v' || k === 'V') { e.stopPropagation(); return; }   // let the paste event fire
        if (k === 'k' || k === 'K') e.preventDefault();
        e.stopPropagation();
        return;
      }
      if (k.length === 1) { e.preventDefault(); e.stopPropagation(); this.liveTypeChar(L, k); return; }
      if (SPECIAL_KEYS.has(k)) { e.preventDefault(); e.stopPropagation(); this.liveEnqueue(L, { t: 'key', key: k }); return; }
      e.stopPropagation();   // Shift/Alt/F-keys etc. — nothing to send, but don't leak to the app
    });
    L.screen.addEventListener('paste', (e) => {
      const text = e.clipboardData ? e.clipboardData.getData('text/plain') : '';
      e.preventDefault();
      e.stopPropagation();
      if (text) this.liveEnqueue(L, { t: 'text', text });
    });
    return L;
  }

  private liveCss(): string {
    // Lives inside each overlay (appended to <body>), so it's styled even
    // while the panel root is being re-rendered.
    return `<style>
      .la-live-bg { position:fixed; inset:0; z-index:9500; background:rgba(15,23,42,.62); backdrop-filter:blur(2px); display:flex; align-items:center; justify-content:center; padding:16px; font-family:var(--font-sans); }
      .la-live { background:var(--card,#fff); color:var(--text,#0f172a); border-radius:14px; box-shadow:0 20px 60px rgba(15,23,42,.35); width:min(1320px, 100%); max-height:100%; display:flex; flex-direction:column; overflow:hidden; }
      .la-live.la-shot { width:min(1100px, 100%); }
      .la-live-head { display:flex; align-items:center; gap:10px; padding:10px 14px; border-bottom:1px solid var(--border,#e2e8f0); }
      .la-live-title { flex:1; min-width:0; font-size:13.5px; font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
      .la-live-state { display:inline-block; margin-left:8px; font-size:11px; font-weight:600; color:var(--text-muted,#64748b); background:var(--bg-subtle,#f8fafc); border:1px solid var(--border,#e2e8f0); border-radius:999px; padding:1px 8px; vertical-align:1px; }
      .la-live-state.live { color:var(--success-text,#065f46); background:var(--success-soft,#ecfdf5); border-color:#a7f3d0; }
      .la-live-state.warn { color:var(--warning-text,#92400e); background:var(--warning-soft,#fffbeb); border-color:#fde68a; }
      .la-live-state.bad { color:var(--danger-text,#991b1b); background:var(--danger-soft,#fef2f2); border-color:#fecaca; }
      .la-live-bar { display:flex; align-items:center; gap:6px; flex-wrap:wrap; padding:8px 14px; border-bottom:1px solid var(--border,#e2e8f0); background:var(--bg-subtle,#f8fafc); }
      .la-live-url { flex:1; min-width:180px; border:1px solid var(--border,#e2e8f0); border-radius:8px; padding:6px 10px; font-family:var(--font-mono, monospace); font-size:11.5px; color:var(--text-muted,#64748b); background:var(--card,#fff); }
      .la-live-url:focus { outline:none; color:var(--text,#0f172a); border-color:var(--accent-border,#bfd7fe); box-shadow:var(--focus-ring); }
      .la-lbtn { display:inline-flex; align-items:center; gap:5px; padding:6px 12px; border:1px solid var(--border,#e2e8f0); border-radius:8px; background:var(--card,#fff); color:var(--text,#0f172a); font:inherit; font-size:12px; font-weight:600; cursor:pointer; white-space:nowrap; }
      .la-lbtn:hover { background:var(--bg-subtle,#f8fafc); border-color:var(--border-strong,#cbd5e1); }
      .la-lbtn.primary { background:var(--text,#0f172a); border-color:var(--text,#0f172a); color:#fff; }
      .la-lbtn.primary:hover { background:#1e293b; }
      .la-live-hint { padding:7px 14px; font-size:12px; color:var(--info-text,#1e40af); background:var(--info-soft,#eff6ff); border-bottom:1px solid var(--border,#e2e8f0); }
      .la-live-screen { position:relative; flex:1; min-height:260px; overflow:auto; background:#0f172a; display:flex; align-items:center; justify-content:center; outline:none; }
      .la-live-type { display:flex; align-items:center; gap:6px; flex-wrap:wrap; padding:8px 14px; border-bottom:1px solid var(--border,#e2e8f0); background:var(--warning-soft,#fffbeb); }
      .la-live-type[hidden] { display:none; }
      .la-live-screen:focus { box-shadow:inset 0 0 0 3px rgba(37,99,235,.75); }
      .la-live-screen img { display:none; max-width:100%; max-height:calc(100vh - 250px); width:auto; height:auto; user-select:none; -webkit-user-select:none; -webkit-user-drag:none; touch-action:none; cursor:default; }
      .la-live-wait { color:#cbd5e1; font-size:13px; line-height:1.5; padding:70px 24px; text-align:center; max-width:560px; }
      .la-live-foot { display:flex; align-items:center; gap:8px; flex-wrap:wrap; padding:10px 14px; border-top:1px solid var(--border,#e2e8f0); }
      .la-live-msg { flex:1; min-width:200px; font-size:12px; color:var(--text-muted,#64748b); line-height:1.4; }
      .la-live-msg.err { color:var(--danger-text,#991b1b); }
      .la-live-msg.ok { color:var(--success-text,#065f46); }
      .la-shot-body { overflow:auto; background:#0f172a; display:flex; justify-content:center; align-items:flex-start; max-height:calc(100vh - 110px); }
      .la-shot-img { display:block; max-width:100%; height:auto; }
      @media (max-width:700px) { .la-live-bg { padding:0; } .la-live { border-radius:0; height:100%; } .la-live-screen img { max-height:calc(100vh - 290px); } }
    </style>`;
  }

  private focusScreen(L: LiveSession): void { try { L.screen.focus({ preventScroll: true }); } catch { /* ignore */ } }
  private liveMsg(L: LiveSession, text: string, tone: Tone): void { L.msg.textContent = text; L.msg.className = `la-live-msg ${tone}`; }
  private liveState(L: LiveSession, text: string): void {
    L.state.textContent = text;
    L.state.className = `la-live-state${text === 'live' ? ' live' : /taken over|in use/.test(text) ? ' warn' : /ended|error|not connected/.test(text) ? ' bad' : ''}`;
  }
  private liveGo(L: LiveSession): void {
    let u = L.urlInput.value.trim();
    if (!u) return;
    if (!/^[a-z][a-z0-9+.-]*:/i.test(u)) u = `https://${u}`;
    if (!/^https?:\/\/\S+$/i.test(u)) { this.liveMsg(L, 'Only http(s) addresses can be opened.', 'err'); return; }
    this.liveEnqueue(L, { t: 'nav', url: u });
    L.urlInput.blur();
    this.focusScreen(L);
  }

  // Cache the access token so the pagehide beacon can go out synchronously.
  // (The global /api fetch wrapper awaits getSession() first, which may not
  // finish while the page is unloading, so the beacon uses an absolute URL —
  // the wrapper only touches '/api/…' — with this token attached directly.)
  private refreshToken(L: LiveSession): void {
    L.tokenAt = Date.now();
    try {
      supabase.auth.getSession()
        .then(({ data }) => { L.token = (data && data.session && data.session.access_token) || ''; })
        .catch(() => { /* beacon just skipped; the server's 60 s frame heartbeat closes the session */ });
    } catch { /* ignore */ }
  }
  private liveBeacon(L: LiveSession): void {
    if (L.closed || L.takenOver || L.notOwner || !L.started || !L.token) return;
    try {
      void fetch(`${location.origin}${API}/agent/live`, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${L.token}` },
        body: JSON.stringify({ platform: L.pid, on: false }),
      }).catch(() => { /* best effort */ });
    } catch { /* best effort */ }
  }
  // Someone else pressed "Take over": stop quietly (no on:false — it's theirs now).
  private liveTakenOver(L: LiveSession, by: any): void {
    if (L.takenOver) return;
    L.takenOver = true;
    L.ended = true;
    if (L.abort) { try { L.abort.abort(); } catch { /* ignore */ } }
    this.liveState(L, 'taken over');
    this.liveMsg(L, `${by ? String(by) : 'Someone else'} took over the live browser.`, 'err');
    if (!L.frames) { L.wait.style.display = ''; L.wait.textContent = 'Someone else has the live browser now.'; }
    void this.load();
  }

  private framePoint(L: LiveSession, cx: number, cy: number): { x: number; y: number } | null {
    const rect = L.img.getBoundingClientRect();
    if (!rect.width || !rect.height) return null;
    const fw = L.w || L.img.naturalWidth, fh = L.h || L.img.naturalHeight;
    if (!fw || !fh) return null;
    const x = Math.round(Math.min(fw - 1, Math.max(0, (cx - rect.left) * fw / rect.width)));
    const y = Math.round(Math.min(fh - 1, Math.max(0, (cy - rect.top) * fh / rect.height)));
    return { x, y };
  }

  // Input pipeline: coalesce typed characters (120 ms) and wheel deltas
  // (120 ms), keep strict order, POST batches through one promise chain.
  private liveEnqueue(L: LiveSession, ev: LiveEvent): void {
    if (L.closed || !L.started) return;
    this.flushText(L);
    this.flushWheel(L);
    L.queue.push(ev);
    this.scheduleSend(L);
  }
  private liveTypeChar(L: LiveSession, ch: string): void {
    if (L.closed || !L.started) return;
    this.flushWheel(L);
    L.textBuf += ch;
    if (L.textTimer) clearTimeout(L.textTimer);
    L.textTimer = setTimeout(() => { L.textTimer = null; this.flushText(L); this.scheduleSend(L); }, 120);
  }
  private flushText(L: LiveSession): void {
    if (L.textTimer) { clearTimeout(L.textTimer); L.textTimer = null; }
    if (L.textBuf) { L.queue.push({ t: 'text', text: L.textBuf }); L.textBuf = ''; }
  }
  private liveWheel(L: LiveSession, dy: number): void {
    this.flushText(L);
    L.wheelDy += dy;
    if (!L.wheelTimer) L.wheelTimer = setTimeout(() => { L.wheelTimer = null; this.flushWheel(L); this.scheduleSend(L); }, 120);
  }
  private flushWheel(L: LiveSession): void {
    if (L.wheelTimer) { clearTimeout(L.wheelTimer); L.wheelTimer = null; }
    if (L.wheelDy) { L.queue.push({ t: 'wheel', dy: Math.round(L.wheelDy) }); L.wheelDy = 0; }
  }
  private scheduleSend(L: LiveSession): void {
    if (L.sendTimer) return;
    L.sendTimer = setTimeout(() => { L.sendTimer = null; this.sendNow(L); }, 16);
  }
  private sendNow(L: LiveSession): void {
    const events = L.queue.splice(0);
    if (!events.length) return;
    L.chain = L.chain.then(() => this.postInput(L, events)).catch(() => { /* reported inside */ });
  }
  private async postInput(L: LiveSession, events: LiveEvent[]): Promise<void> {
    const r = await this.req('POST', `${API}/agent/live/${encodeURIComponent(L.pid)}/input`, { events });
    if (r.ok || L.closed) return;
    if (r.status === 409 && r.data && r.data.code === 'in_use') this.liveTakenOver(L, r.data.by);
    else if (r.status === 409) { L.ended = true; this.liveState(L, 'ended'); this.liveMsg(L, 'The live session ended — close and reopen it to continue.', 'err'); }
    else this.liveMsg(L, `Input not delivered — ${r.error}`, 'err');
  }

  private liveAttached(L: LiveSession): boolean {
    if (!L.overlay.isConnected) return false;
    if (this.rootEl && this.rootEl.isConnected) { L.detachedSince = 0; return true; }
    // brief grace: MtrPage re-renders swap the root for a moment
    if (!L.detachedSince) L.detachedSince = Date.now();
    return Date.now() - L.detachedSince < 8000;
  }

  private async frameLoop(L: LiveSession): Promise<void> {
    let backoff = 0;
    while (!L.closed && this.live === L && !L.ended) {
      if (!this.liveAttached(L)) { void this.closeLive(L, 'gone'); return; }
      // Keep pulling frames even while this tab is hidden (someone fetching a
      // 2FA code from their email) — the server ends a session after 60 s
      // without a frame request. Just slower.
      if (document.hidden) await this.sleep(2500);
      if (L.closed || L.ended) return;
      if (Date.now() - L.tokenAt > 4 * 60000) this.refreshToken(L);
      const t0 = Date.now();
      const ctrl = new AbortController();
      L.abort = ctrl;
      let r: Response | null = null;
      try { r = await fetch(`${API}/agent/live/${encodeURIComponent(L.pid)}/frame?after=${encodeURIComponent(String(L.seq))}`, { signal: ctrl.signal, cache: 'no-store' }); }
      catch { r = null; }
      L.abort = null;
      if (L.closed) return;
      if (!r) {
        backoff = Math.min(5000, backoff + 1000);
        this.liveState(L, 'reconnecting…');
        await this.sleep(backoff);
        continue;
      }
      if (r.status === 200) {
        backoff = 0;
        const blob = await r.blob().catch(() => null);
        if (L.closed) return;
        if (blob && blob.size) {
          const url = URL.createObjectURL(blob);
          const old = L.objUrl;
          L.objUrl = url;
          L.img.src = url;
          if (old) URL.revokeObjectURL(old);
          const seq = Number(r.headers.get('X-Frame-Seq'));
          if (Number.isFinite(seq)) L.seq = seq;
          const w = Number(r.headers.get('X-Frame-W')), h = Number(r.headers.get('X-Frame-H'));
          if (w > 0 && h > 0) { L.w = w; L.h = h; }
          const u = r.headers.get('X-Frame-Url');
          if (u != null && document.activeElement !== L.urlInput) {
            let dec = u;
            try { dec = decodeURIComponent(u); } catch { /* leave encoded */ }
            L.urlInput.value = dec;
          }
          L.frames++;
          if (L.frames === 1) { L.img.style.display = 'block'; L.wait.style.display = 'none'; }
          this.liveState(L, 'live');
        }
        const spent = Date.now() - t0;
        if (spent < 350) await this.sleep(350 - spent);
      } else if (r.status === 204) {
        backoff = 0;
        if (L.frames) this.liveState(L, 'live');
        const spent = Date.now() - t0;
        if (spent < 300) await this.sleep(300 - spent);   // the server long-polls ~1.5 s; never hammer if it doesn't
      } else if (r.status === 409) {
        const j = await r.json().catch(() => null);
        if (L.closed) return;
        if (j && j.code === 'in_use') { this.liveTakenOver(L, j.by); return; }
        L.ended = true;
        this.liveState(L, 'ended');
        this.liveMsg(L, `Live session ended${j && j.error ? ` — ${j.error}` : ''}. Close and reopen to continue.`, 'err');
        if (!L.frames) { L.wait.style.display = ''; L.wait.textContent = 'Live session ended.'; }
        return;
      } else {
        const j = await r.json().catch(() => null);
        if (L.closed) return;
        backoff = Math.min(5000, backoff + 1000);
        this.liveState(L, `error (HTTP ${r.status}) — retrying…`);
        if (j && j.error) this.liveMsg(L, String(j.error), 'err');
        await this.sleep(backoff);
      }
    }
  }

  private async closeLive(L: LiveSession, how: 'close' | 'done' | 'gone'): Promise<void> {
    if (L.closed) return;
    // deliver anything still buffered (a code typed just before Done) first
    this.flushText(L);
    this.flushWheel(L);
    if (L.sendTimer) { clearTimeout(L.sendTimer); L.sendTimer = null; }
    this.sendNow(L);
    L.closed = true;
    if (L.onHide) { window.removeEventListener('pagehide', L.onHide); L.onHide = null; }
    if (L.abort) { try { L.abort.abort(); } catch { /* ignore */ } }
    L.overlay.remove();
    if (L.objUrl) { URL.revokeObjectURL(L.objUrl); L.objUrl = null; }
    if (this.live === L) this.live = null;
    const chain = Promise.race([L.chain, this.sleep(1500)]);
    // Closing turns live mode off — unless the session is someone else's now
    // (they took over, or our start was refused as in use).
    const off = L.takenOver || L.notOwner
      ? chain.then(() => null)
      : chain.then(() => this.req('POST', `${API}/agent/live`, { platform: L.pid, on: false }));
    if (how === 'gone') { void off; return; }
    if (how === 'done') this.notes[L.pid] = { text: 'Thanks — the agent takes it from here. This card updates within a minute.', tone: 'ok', at: Date.now() };
    this.patchPlats();
    await off;
    if (this.rootEl && this.rootEl.isConnected) await this.load();
  }

  // ── styles ────────────────────────────────────────────────────────────────
  private css(): string {
    return `<style>
      .la { display:flex; flex-direction:column; gap:14px; color:var(--text); }
      .la-card { background:var(--card); border:1px solid var(--border); border-radius:12px; box-shadow:var(--shadow-card); overflow:hidden; min-width:0; }
      .la-card-h { display:flex; align-items:center; gap:10px; flex-wrap:wrap; padding:11px 16px; border-bottom:1px solid var(--border); font-size:13px; font-weight:700; }
      .la-card-b { padding:14px 16px; }
      .la-meta { font-size:12px; color:var(--text-muted); font-weight:400; line-height:1.45; }
      .la-faint { color:var(--text-faint); }
      .la-nowrap { white-space:nowrap; }
      .la-num { font-variant-numeric:tabular-nums; }
      .la-btn { display:inline-flex; align-items:center; justify-content:center; gap:5px; padding:7px 14px; border:1px solid var(--border); border-radius:var(--radius-btn, 10px); background:var(--card); color:var(--text); font:inherit; font-size:12.5px; font-weight:600; line-height:1.3; cursor:pointer; white-space:nowrap; transition:background .12s ease, border-color .12s ease; }
      .la-btn:hover { background:var(--bg-subtle); border-color:var(--border-strong); }
      .la-btn.primary { background:var(--text); border-color:var(--text); color:#fff; }
      .la-btn.primary:hover { background:#1e293b; }
      .la-btn.sm { padding:5px 11px; font-size:11.5px; border-radius:8px; }
      .la-btn:disabled { opacity:.45; cursor:default; }
      .la-chip { display:inline-flex; align-items:center; font-size:10.5px; font-weight:700; padding:2px 9px; border-radius:999px; white-space:nowrap; background:var(--bg-subtle); color:var(--text-muted); border:1px solid var(--border); }
      .la-chip.ok { background:var(--success-soft); color:var(--success-text); border-color:var(--success-border, #a7f3d0); }
      .la-chip.warn { background:var(--warning-soft); color:var(--warning-text); border-color:#fde68a; }
      .la-chip.bad { background:var(--danger-soft); color:var(--danger-text); border-color:var(--danger-border, #fecaca); }
      .la-note { font-size:12px; color:var(--text-muted); line-height:1.4; }
      .la-note.err { color:var(--danger-text); }
      .la-note.ok { color:var(--success-text); }
      .la-callout { display:flex; gap:10px; align-items:flex-start; padding:10px 14px; border-radius:12px; font-size:12.5px; line-height:1.5; }
      .la-callout.warn { background:var(--warning-soft); border:1px solid #fde68a; color:var(--warning-text); }

      .la-head { display:flex; align-items:center; gap:14px; flex-wrap:wrap; padding:14px 16px; }
      .la-dot { width:11px; height:11px; border-radius:50%; background:var(--text-faint); flex:none; }
      .la-dot.on { background:#10b981; box-shadow:0 0 0 4px rgba(16,185,129,.16); }
      .la-head-t { flex:1; min-width:200px; }
      .la-head-title { font-size:15px; font-weight:700; letter-spacing:-.01em; display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
      .la-head-side { display:flex; align-items:center; gap:10px; }
      .la-intro { flex:0 0 100%; margin:2px 0 0; font-size:12.5px; color:var(--text-muted); line-height:1.55; }

      .la-setup-d > summary { cursor:pointer; list-style:none; border-bottom:none; }
      .la-setup-d > summary::-webkit-details-marker { display:none; }
      .la-setup-d > summary::before { content:'▸'; color:var(--text-faint); font-size:11px; }
      .la-setup-d[open] > summary { border-bottom:1px solid var(--border); }
      .la-setup-d[open] > summary::before { content:'▾'; }
      .la-steps { margin:0; padding-left:22px; display:flex; flex-direction:column; gap:14px; }
      .la-steps li { font-size:12.5px; }
      .la-step-t { font-weight:700; font-size:13px; margin-bottom:3px; }
      .la-code { margin:6px 0 8px; padding:9px 12px; background:#0f172a; color:#e2e8f0; border-radius:8px; font-family:var(--font-mono); font-size:12px; line-height:1.55; overflow-x:auto; white-space:pre; }
      .la-steps code, .la-help code { font-family:var(--font-mono); font-size:11.5px; background:var(--bg-subtle); border:1px solid var(--border); border-radius:5px; padding:0 4px; }
      .la-code code { background:none; border:none; padding:0; color:inherit; font-size:inherit; }

      .la-plats { display:grid; grid-template-columns:repeat(3, minmax(0,1fr)); gap:12px; }
      .la-plat { padding:14px 16px; display:flex; flex-direction:column; gap:8px; }
      .la-plat-top { display:flex; align-items:center; gap:8px; }
      .la-plat-name { flex:1; min-width:0; font-size:14px; font-weight:700; }
      .la-busy { color:var(--info-text); font-weight:600; }
      .la-plat-msg { font-size:12px; background:var(--bg-subtle); border:1px solid var(--border-soft); border-radius:8px; padding:6px 9px; line-height:1.45; overflow-wrap:anywhere; }
      .la-plat-live { font-size:12px; font-weight:600; color:var(--danger-text); }
      .la-kv { display:grid; grid-template-columns:auto 1fr; gap:3px 12px; font-size:12px; margin:0; }
      .la-kv dt { color:var(--text-muted); }
      .la-kv dd { margin:0; font-variant-numeric:tabular-nums; overflow-wrap:anywhere; }
      .la-plat-actions { display:flex; flex-wrap:wrap; gap:6px; margin-top:auto; padding-top:4px; }

      .la-cols { display:grid; grid-template-columns:minmax(0,1.7fr) minmax(0,1fr); gap:14px; align-items:start; }
      .la-form { display:flex; flex-direction:column; gap:10px; }
      .la-sub-h { font-size:10.5px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:var(--text-faint); margin-top:6px; }
      .la-switch { display:flex; align-items:flex-start; gap:10px; cursor:pointer; }
      .la-switch input { margin-top:3px; }
      .la-switch > span { display:flex; flex-direction:column; gap:1px; font-size:13px; }
      .la-fgrid { display:grid; grid-template-columns:repeat(3, minmax(0,1fr)); gap:10px 14px; }
      .la-fgrid label { display:flex; flex-direction:column; gap:4px; font-size:11px; font-weight:600; color:var(--text-muted); min-width:0; }
      .la-in { border:1px solid var(--border); border-radius:8px; padding:6px 9px; font:inherit; font-size:12px; color:var(--text); background:var(--card); min-width:0; box-sizing:border-box; }
      .la-in:focus { outline:none; border-color:var(--accent-border); box-shadow:var(--focus-ring); }
      .la-in:disabled { background:var(--bg-subtle); color:var(--text-muted); }
      .la-url { width:100%; min-width:180px; font-family:var(--font-mono); font-size:11.5px; }
      .la-inline { display:inline-flex; align-items:center; gap:7px; font-size:12.5px; cursor:pointer; }
      .la-checks { display:flex; flex-wrap:wrap; gap:8px 20px; }
      .la-help { margin-top:-2px; }
      .la-savebar { display:flex; align-items:center; gap:8px; padding:10px 16px; border-top:1px solid var(--border); background:var(--bg-subtle); }
      .la-savebar .la-note { flex:1; }

      .la-tbl-wrap { overflow-x:auto; }
      .la-runs { max-height:380px; overflow-y:auto; }
      table.la-tbl { width:100%; border-collapse:collapse; font-size:12px; }
      .la-tbl th { position:sticky; top:0; background:var(--card); text-align:left; font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); padding:8px 12px; border-bottom:1px solid var(--border); white-space:nowrap; }
      .la-tbl td { padding:7px 12px; border-bottom:1px solid var(--border-soft); vertical-align:middle; }
      .la-tbl tr:last-child td { border-bottom:none; }
      .la-ptbl th, .la-ptbl td { padding-left:0; }
      .la-ptbl-name { font-weight:600; white-space:nowrap; }
      .la-run-msg { max-width:380px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

      .la-usage { display:grid; grid-template-columns:1fr 1fr; gap:12px; }
      .la-u-l { font-size:10.5px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:var(--text-muted); }
      .la-u-v { font-size:22px; font-weight:700; letter-spacing:-.02em; font-variant-numeric:tabular-nums; margin:2px 0 1px; }
      .la-bars { display:flex; align-items:flex-end; gap:3px; height:56px; margin-top:16px; }
      .la-bar-c { flex:1; height:100%; display:flex; align-items:flex-end; }
      .la-bar { width:100%; background:var(--accent-border); border-radius:3px 3px 0 0; }
      .la-bar.last { background:var(--accent); }
      .la-bar.zero { background:var(--border); }
      .la-bars-x { display:flex; justify-content:space-between; font-size:10.5px; color:var(--text-faint); margin-top:4px; }

      .la-empty { padding:40px 24px; text-align:center; }
      .la-empty-ic { font-size:30px; margin-bottom:6px; }
      .la-empty h3 { margin:0 0 6px; font-size:15px; }
      .la-empty p { margin:0 auto 14px; max-width:520px; font-size:13px; color:var(--text-muted); }

      @media (max-width:1100px) { .la-plats { grid-template-columns:repeat(2, minmax(0,1fr)); } .la-cols { grid-template-columns:minmax(0,1fr); } }
      @media (max-width:700px) { .la-plats { grid-template-columns:minmax(0,1fr); } .la-fgrid { grid-template-columns:repeat(2, minmax(0,1fr)); } .la-usage { grid-template-columns:1fr 1fr; } }
    </style>`;
  }
}
